import { FilterBase } from "@components/commerce/product-listing/filters/common/filter/FilterBase";
import { FiltersContext } from "@components/commerce/product-listing/filters/context/FiltersContext";
import type { IPartSortByOption } from "@contentful-api/types/contentful";
import { useAlgoliaClient } from "@hooks/useAlgoliaClient";
import { AnalyticsClient } from "@lib/analytics/AnalyticsClient";
import { Radio } from "@ui/components/forms/radio/Radio";
import { Box } from "@ui/components/layout/box/Box";
import { SelectDropdownItem } from "@ui/components/overlay/select-dropdown/SelectDropdown";
import { useContext, useEffect } from "react";
import { useSortBy } from "react-instantsearch";
import { dropdownItemStyle } from "./sortByStyles";
import { useThemeMediaQuery } from "@ui/hooks/useThemeMediaQuery";
import { useMicrocopy } from "@lib/contentful/microcopy/MicrocopyContext";
import { CommonCMS } from "@lib/constants/contentful";

interface SortByProps {
    locale: string;
}

export const SortBy = ({ locale }: SortByProps) => {
    const { indexName } = useAlgoliaClient(locale);
    const { setSortByOptions, sortByOptionsList, setMobileSortByOpen } = useContext(FiltersContext);

    const { options, currentRefinement, refine } = useSortBy({
        items: getSortByOptions(indexName, sortByOptionsList),
    });
    const { get: getMicrocopy } = useMicrocopy();
    const { lg } = useThemeMediaQuery();

    useEffect(() => {
        setSortByOptions({ options, currentRefinement, refine });
    }, [currentRefinement, options, refine, setSortByOptions]);

    const onOpen = () => {
        if (lg) return;
        setMobileSortByOpen(true);
    };
    const onClose = () => {
        if (lg) return;
        setMobileSortByOpen(false);
    };

    return sortByOptionsList?.length ? (
        <Box>
            <FilterBase
                closeOnSelect={true}
                onOpen={onOpen}
                onClose={onClose}
                placement={lg ? "bottom-start" : "bottom-end"}
                filterName={getMicrocopy(CommonCMS.page, CommonCMS.PLP.sortByLabel)}
                dropdownItems={
                    <>
                        {options?.map((item) => (
                            <SelectDropdownItem
                                key={item.label}
                                onClick={() => {
                                    AnalyticsClient.listSortingApplied(item.label);
                                    refine(item.value);
                                }}
                                sx={dropdownItemStyle}
                            >
                                <RadioWithHoveredState
                                    item={item}
                                    currentRefinement={currentRefinement}
                                />
                            </SelectDropdownItem>
                        ))}
                    </>
                }
            />
        </Box>
    ) : null;
};

const RadioWithHoveredState = ({ item, currentRefinement }) => {
    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
                width: "100%",
                _hover: {
                    ".chakra-radio__control": {
                        borderColor: "black",
                    },
                },
            }}
        >
            <Radio
                value={item.value}
                colorScheme={"black"}
                isChecked={item.value === currentRefinement}
                fontWeight={"bold"}
            >
                {item.label}
            </Radio>
        </Box>
    );
};

export const getSortByOptions = (indexName: string, sortByOptionsList: IPartSortByOption[]) => {
    return (
        sortByOptionsList?.map((item) => {
            return {
                label: item?.fields?.label,
                value:
                    item?.fields?.algoliaIndex === "default"
                        ? indexName
                        : `${indexName}${item?.fields?.algoliaIndex}`,
            };
        }) ?? []
    );
};

export default SortBy;
